import "./alertcase.scss";
import { useContext, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { BsArrowLeft, BsSave, BsXLg } from "react-icons/bs";
import { FaPencilAlt } from "react-icons/fa";
import { MdOutlineKeyboardArrowDown, MdOutlineKeyboardArrowUp } from "react-icons/md";
import { useParams, useNavigate } from "react-router";

import ConfirmationPopup from "../../../shared/components/confirmation-popup/confirmation-popup";
import Spinner from "../../../shared/components/spinner/spinner";
import TextInput from "../../../shared/components/text-input/text-input";
import ToolTip from "../../../shared/components/tooltip/tooltip";
import { roleWrite } from "../../../shared/constants/user-roles";
import AuthContext from "../../../shared/context/auth-context";
import useHttp from "../../../shared/hooks/use-http";
import { useHttpGet } from "../../../shared/hooks/use-httpNonTable";
import { failedToast, getData, postSingle, postSingleNonTable, succesfulToast } from "../../../shared/utils/api";
import { useBack } from "../../../shared/utils/use-back";
import { usePrompt } from "../../../shared/utils/use-prompt";
import { IObject, validateLoadingStatusNew } from "../../../shared/utils/utils";
import MultiTabComponent, { ITab } from "../../components/multi-tab-component/multi-tab-component";
import ReadonlyWidgetComponent from "../../components/widgets-layout/readonly-widget-component/readonly-widget-component";
import { IWidgetLayout } from "../../components/widgets-layout/widget-component/widget-component";
import { Widget } from "../../components/widgets-layout/widget-utils";
import { IFieldWidget } from "../../components/widgets-layout/widgets/text-field-widget/text-field-widget";
import Notes from "./notes/notes";
import Network from "./network/network";
import History from "./history/history";
import Documents from "./documents/documents";
import AssignUserPopup from "./assign_user_popup";
import Actions from "./actions/actions";

interface IAlertCaseInfo {
  id: string;
  title: string;
  alertcase_number: string;
  domain_type_id: string;
  process_type_id: string;
  event_id: string;
  creation_date: string;
  consolidate_on_entity_id?: string;
  consolidate_on_entity_type_id?: string;
  consolidate_on_entity_type?: string;
  current_process_type_stage_id: string;
  domain_type: string;
  process_type: string;
  current_stage_id: string;
  current_stage_name: string;
  next_stage_id: string;
  state: string;
  alertcase_id: string;
  alertcase_type_id: string;
  alertcase_type_name?: string;
  assigned_to?: string;
  assigned_to_name?: string;
  is_user_allowed?: boolean;
}

const AlertCase = () => {
  const authCtx = useContext(AuthContext);
  const params = useParams();
  const navigate = useNavigate();
  
  const { handleBack } = useBack();

  const [alertCaseInfo, setAlertCaseInfo] = useState<IAlertCaseInfo | undefined>();
  const [layout, setLayout] = useState<IWidgetLayout | undefined>();
  const [consolidationLayout, setConsolidationLayout] = useState<IWidgetLayout | undefined>();
  const [datamodelData, setDatamodelData] = useState<IObject>({});
  const [consolidatingEntity, setConsolidatingEntity] = useState<IObject>({});
  const [navigateAfterSave, setNavigateAfterSave] = useState<boolean>(false);
  const [assignUserModalVisible, setAssignUserModalVisible] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [consolidateOnEntityIsEditable, setConsolidateOnEntityIsEditable] = useState<boolean>(false);
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [isActionsDone, setIsActionsDone] = useState<boolean>(false);
  const [canFinishAlertCase, setCanFinishAlertCase] = useState<boolean>(false);
  const [newConsolidatingEntityData, setNewConsolidatingEntityData] = useState<any>({}); // eslint-disable-line
  const [consolidatingEntityToast, setConsolidatingEntityToast] = useState<string>("");
  const [isRequiredsDone, setIsRequiredsDone] = useState<boolean> (false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const { flag, setFlag, next } = usePrompt(isDirty);
  const [blockInput, setBlockInput] = useState<boolean>(true);

  const { sendRequest: getAlertCase, status: getAlertCaseStatus, data: getAlertCaseData, error: getAlertCaseError } = useHttpGet(getData, false, authCtx.token, "alertcaseRecord", "manager");
  const { sendRequest: getDomainType, status: getDomainTypeStatus, data: getDomainTypeData, error: getDomainTypeError } = useHttpGet(getData, false, authCtx.token, "domainProcessType", "manager");
  const { sendRequest: getScreen, status: getScreenStatus, data: getScreenData, error: getScreenError } = useHttpGet(getData, false, authCtx.token, "screen", "manager");
  const { sendRequest: getCapture, status: getCaptureStatus, data: getCaptureData, error: getCaptureError } = useHttpGet(getData, false, authCtx.token, "capture", "manager");
  const { sendRequest: getStageActionCheck, status: getStageActionCheckStatus, data: getStageActionCheckData, error: getStageActionCheckError } = useHttpGet(getData, false, authCtx.token, "default", "manager");
  const { sendRequest: getFinishAlertCaseCheck, status: getFinishAlertCaseCheckStatus, data: getFinishAlertCaseCheckData, error: getFinishAlertCaseCheckError } = useHttpGet(getData, false, authCtx.token, "default", "manager");
  const { sendRequest: updateAlertCaseStage, status: updateAlertCaseStageStatus, error: updateAlertCaseStageError } = useHttp(postSingleNonTable, false, authCtx.token, "default", "manager");
  const { sendRequest: postAlertRowData, status: postAlertRowDataStatus, data: postAlertRowDataResult, error: postAlertRowDataError } = useHttp(postSingle, false, authCtx.token, "datamodelRowData", "platform");
  const { sendRequest: updateAlertCaseState, status: updateAlertCaseStateStatus, error: updateAlertCaseStateError } = useHttpGet(getData, false, authCtx.token, "default", "manager");
  const { sendRequest: postAlertCase, status: postAlertCaseStatus, data: postAlertCaseResult, error: postAlertCaseError } = useHttp(postSingleNonTable, false, authCtx.token, "default", "manager");
  const { sendRequest: getAlertData, status: getAlertCaseDataStatus, data: alertCaseData, error: getAlertCaseDataError } = useHttp(postSingle, false, authCtx.token, "datamodelTable", "platform");
  const { sendRequest: updateAlertCaseAssignUser, status: updateAlertCaseAssignUserStatus, error: updateAlertCaseAssignUserError } = useHttpGet(getData, false, authCtx.token, "default", "manager");
  const { sendRequest: getConsolidatingEntityData, status: getConsolidatingEntityDataStatus, data: consolidatingEntityData, error: getConsolidatingEntityDataError } = useHttp(postSingle, false, authCtx.token, "datamodelTable", "platform");
  const { sendRequest: postConsolidatingEntityData, status: postConsolidatingEntityDataStatus, error: postConsolidatingEntityDataError } = useHttp(postSingle, false, authCtx.token, "datamodelRowData", "platform");
  const { sendRequest: postAuditLog } = useHttp(postSingleNonTable, false, authCtx.token, "default", "manager");

  useEffect(() => {
    if (getStageActionCheckStatus === "completed" && !getStageActionCheckError && getStageActionCheckData) {
      if (getStageActionCheckData.success) {
        setIsActionsDone(true);
      }
    }
  }, [getStageActionCheckStatus, getStageActionCheckData]);

  useEffect(() => {
    if (postConsolidatingEntityDataStatus === "completed" && !postConsolidatingEntityDataError && getAlertCaseData?.consolidate_on_entity_type_id) {
      getConsolidatingEntityData(`datamodel/table/${getAlertCaseData.consolidate_on_entity_type_id}/entity`, {
        ids: [getAlertCaseData.consolidate_on_entity_id],
        columns: []
      }, undefined, "none");
    }
  }, [postConsolidatingEntityDataStatus, postConsolidatingEntityDataError]);

  useEffect(() => {
    const rolesArray = authCtx.selectedTenant.roles;
    if (rolesArray.indexOf(roleWrite) !== -1) {
      setBlockInput(false);
    }
  }, [authCtx.selectedTenant, authCtx.systemAdmin]);

  useEffect(() => {
    getAlertCase(`alertcase/${params.id}`);
    getFinishAlertCaseCheck(`finish_alertcase_check/${params.id}`);
  }, []);

  useEffect(() => {
    if (getFinishAlertCaseCheckStatus === "completed" && !getFinishAlertCaseCheckError && getFinishAlertCaseCheckData) {
      setCanFinishAlertCase(getFinishAlertCaseCheckData.success)
    }
  }, [getFinishAlertCaseCheckStatus, getFinishAlertCaseCheckError, getFinishAlertCaseCheckData]);

  useEffect(() => {
    if (getConsolidatingEntityDataStatus === "completed" && !getConsolidatingEntityDataError && consolidatingEntityData) {
      setNewConsolidatingEntityData(consolidatingEntityData);
    }
  }, [getConsolidatingEntityDataStatus, getConsolidatingEntityDataError, consolidatingEntityData]);

  useEffect(() => {
    if (getAlertCaseStatus === "completed" && !getAlertCaseError && getAlertCaseData && getAlertCaseData.current_process_type_stage_id && getAlertCaseData.state === "Open") {
      updateAlertCaseState(`alertcase/state_change/${getAlertCaseData.id}/${getAlertCaseData.current_process_type_stage_id}/Active`);
    } 
    if (getAlertCaseStatus === "completed" && getAlertCaseError) {
      failedToast("You are not allowed to view this stage.");
    } 
    if (getAlertCaseStatus === "completed" && !getAlertCaseError && getAlertCaseData && getAlertCaseData.current_process_type_stage_id) {
      getStageActionCheck(`stage_action_check/${getAlertCaseData.id}/${getAlertCaseData.current_process_type_stage_id}`);
    }
  }, [getAlertCaseStatus, getAlertCaseData, getAlertCaseError]);

  useEffect(() => {
    if (getAlertCaseStatus === "completed" && !getAlertCaseError && getAlertCaseData) {
      setIsAuthorized(true);
      setAlertCaseInfo(getAlertCaseData);
      if (getAlertCaseData.consolidate_on_entity_id){
        getCapture(`datamodel_capture/${getAlertCaseData.consolidate_on_entity_type_id}/entity`);
        getConsolidatingEntityData(`datamodel/table/${getAlertCaseData.consolidate_on_entity_type_id}/entity`, {
          ids: [getAlertCaseData.consolidate_on_entity_id],
          columns: []
        }, undefined, "none");
      }
      getAlertData(`datamodel/table/${getAlertCaseData.alertcase_type_id}/alertcase`, {
        ids: [getAlertCaseData.alertcase_id],
        columns: []
      }, undefined, "none");
      getDomainType(`domain_type/${getAlertCaseData.domain_type_id}`);
    }
  }, [getAlertCaseStatus, getAlertCaseError, getAlertCaseData]);

  useEffect(() => {
    if (getCaptureStatus === "completed" && !getCaptureError && getCaptureData && getConsolidatingEntityDataStatus === "completed" && !getConsolidatingEntityDataError && consolidatingEntityData) {
      setConsolidationLayout(getCaptureData.layout);
      setConsolidatingEntity({...consolidatingEntityData.rows[0]});
    }
  }, [getCaptureStatus, getCaptureError, getCaptureData, getConsolidatingEntityDataStatus, getConsolidatingEntityDataError, consolidatingEntityData]);

  useEffect(() => {
    if (getAlertCaseDataStatus === "completed" && !getAlertCaseDataError && alertCaseData ) {
      setDatamodelData({...alertCaseData.rows[0], 
        relatedEntityTypeId: alertCaseInfo?.consolidate_on_entity_type_id, 
        relatedEntityId: alertCaseInfo?.consolidate_on_entity_id,
        alertcaseRowId: alertCaseInfo?.alertcase_id,
        alertcaseId: alertCaseInfo?.id
      });
    }
  }, [getAlertCaseDataStatus, getAlertCaseDataError, alertCaseData]);

  useEffect(() => {
    if (getDomainTypeStatus === "completed" && !getDomainTypeError && getDomainTypeData && alertCaseInfo?.current_stage_id) {
      const currentStage = getDomainTypeData.stages.find((stage: { id: string }) => stage.id === alertCaseInfo.current_stage_id);
      if (currentStage) {
        getScreen(`screen/${currentStage.screen_id}`);
      }
    }
  }, [getDomainTypeStatus, getDomainTypeError, getDomainTypeData]);
  
  useEffect(() => {
    if (requiredFieldsFilledIn().length === 0) {
      setIsRequiredsDone(true);
    } else {
      setIsRequiredsDone(false);
    }
  }, [datamodelData, layout]);

  useEffect(() => {
    if (getScreenData && getScreenStatus === "completed" && !getScreenError) {
      const updatedLayout = structuredClone(getScreenData.layout);

      updatedLayout.layoutItems = updatedLayout.layoutItems.map((item: Widget) => {
        if (item.modalType === "datamodelTable") {
          item.dataUrl.payload.alertcase_id = alertCaseInfo?.id;
        }
        return item;
      });
  
      setLayout(updatedLayout);
    }
  }, [getScreenData, getScreenError, getScreenStatus]);

  useEffect(() => {
    if (updateAlertCaseStageStatus === "completed" && !updateAlertCaseStageError && !postAlertCaseError && postAlertCaseResult && postAlertRowDataStatus === "completed" && !postAlertRowDataError && postAlertRowDataResult) {
      handleBack();
    }
  }, [updateAlertCaseStageStatus, updateAlertCaseStageError, postAlertCaseStatus, postAlertCaseError, postAlertCaseResult, postAlertRowDataStatus, postAlertRowDataResult, postAlertRowDataError]);

  useEffect(() => {
    if (navigateAfterSave && postAlertRowDataStatus === "completed" && !postAlertRowDataError && postAlertRowDataResult && updateAlertCaseStateStatus === "completed" && !updateAlertCaseStateError &&
      (postAlertCaseStatus ? (postAlertCaseStatus === "completed" && !postAlertCaseError && postAlertCaseResult) : true)) {
      handleBack();
    }
  }, [postAlertCaseStatus, postAlertCaseError, postAlertCaseResult, postAlertRowDataStatus, postAlertRowDataResult, postAlertRowDataError, updateAlertCaseStateStatus, updateAlertCaseStateError]);

  useEffect(() => {
    if (updateAlertCaseAssignUserStatus === "completed" && !updateAlertCaseAssignUserError) {
      handleBack();
    }
  }, [updateAlertCaseAssignUserStatus, updateAlertCaseAssignUserError]);

  useEffect(() => {
    if (postConsolidatingEntityDataStatus === "completed" && postAlertRowDataStatus === "completed" && postAlertCaseStatus === "completed") {
      succesfulToast("Saved successfully");
    }
  }, [postAlertRowDataStatus, postAlertCaseStatus, postConsolidatingEntityDataStatus]);

  const handleNextStage = () => {
    if (!alertCaseInfo) {
      return;
    }
    setIsDirty(false);
    const requiredFields = requiredFieldsFilledIn();
    if (requiredFields.length > 0) {
      failedToast(`Required fields are not filled in: ${requiredFields.join(", ")}`);
      setIsRequiredsDone(false);
      return;
    }
    setNavigateAfterSave(true);
    setIsRequiredsDone(true);
    saveAlertCase();
    updateAlertCaseStage(`update_alertcase_stage/${alertCaseInfo.id}`, {
      current_stage_id: alertCaseInfo.current_stage_id,
      process_type_stage_id: alertCaseInfo.current_process_type_stage_id
    }, undefined, "none");
  };

  const requiredFieldsFilledIn = () => {
    if (!layout || !layout.layoutItems || !datamodelData) {
      return [];
    }
    const requiredFields = layout.layoutItems.filter((item: Widget): boolean =>
      !item.id ||
      Object.prototype.hasOwnProperty.call(item, "required") &&
      (item as IFieldWidget).required === true
    );
    const allRequiredFieldsNotFilled = requiredFields.filter((item) => item.id !== undefined && !datamodelData[item.id]).map(item => item.id);
    if (!alertCaseInfo?.title) {
      return [...allRequiredFieldsNotFilled, "AlertCase Title"];
    }
    return allRequiredFieldsNotFilled;
  };
  
  const handleReOpenAlertCase = () => {
    updateAlertCaseState(`alertcase/state_change/${alertCaseInfo?.id}/${alertCaseInfo?.current_process_type_stage_id}/Open`).then(() => {
      handleBack();
    });
  };

  const saveAlertCase = () => {
    if (!alertCaseInfo) {
      return;
    }
    postAlertRowData("datamodel/record_import", {
      id: alertCaseInfo.alertcase_type_id,
      datamodel_type: "alertcase",
      row_data: JSON.stringify(datamodelData),
      rowData: []
    }, undefined, "none");

    if (!alertCaseInfo?.title) {
      return;
    }

    postAlertCase(`alertcase/${alertCaseInfo.id}`, {
      id: alertCaseInfo.id,
      title: alertCaseInfo.title
    }, undefined, "none");
  };

  useEffect(() => {
    if(alertCaseInfo && postAlertRowDataStatus === "completed" && !postAlertRowDataError && postAlertRowDataResult) {
      // alertCaseData datamodelData
      const backendAlertCaseData = alertCaseData.rows[0];
      const changedFields = Object.keys(backendAlertCaseData).filter(key => backendAlertCaseData[key] !== datamodelData[key]);
      const changedFieldsString = changedFields.map(key => `${key}: ${backendAlertCaseData[key]} -> ${datamodelData[key]}`).join(", ");
      if (changedFields.length > 0) {
        postAuditLog(`audit_trails/${alertCaseInfo.id}`, {action: `Update alertcase with changed fields: ${changedFieldsString}`}, undefined, "none");
      }
    }
  }, [postAlertRowDataStatus, postAlertRowDataError, postAlertRowDataResult]);

  const saveConsolidatingEntity = () => {
    if (!alertCaseInfo) {
      return;
    }
    postConsolidatingEntityData("datamodel/record_import", {
      id: alertCaseInfo.consolidate_on_entity_type_id,
      datamodel_type: "entity",
      row_data: JSON.stringify(newConsolidatingEntityData.rows[0]),
      rowData: []
    }, undefined, "none");
    setConsolidateOnEntityIsEditable(false);
  };

  const handleSave = (action: "close" | "save" | "saveClose" | "finish") => {
    setNavigateAfterSave(action === "close" || action === "saveClose" || action === "finish");
    switch (action) {
      case "close":
        updateAlertCaseState(`alertcase/state_change/${alertCaseInfo?.id}/${alertCaseInfo?.current_process_type_stage_id}/Open`).then(() => {
          handleBack();
        });
        break;
      case "finish":
      {
        const requiredFields = requiredFieldsFilledIn();

        if (requiredFields.length > 0 && action === "finish") {
          failedToast(`Required fields are not filled in: ${requiredFields.join(", ")}`);
          break;
        }
        saveAlertCase();
        handleSaveConsolidatingEntity(); 
        updateAlertCaseState(`alertcase/state_change/${alertCaseInfo?.id}/${alertCaseInfo?.current_process_type_stage_id}/Finished`);
        break;
      }
      case "save":
        saveAlertCase();
        handleSaveConsolidatingEntity();
        break;
      case "saveClose":
        saveAlertCase();
        handleSaveConsolidatingEntity();
        updateAlertCaseState(`alertcase/state_change/${alertCaseInfo?.id}/${alertCaseInfo?.current_process_type_stage_id}/Open`);
        break;
      default:
        break;
    }
    setIsDirty(false);
  };

  const handleDataChanged = (newValue: string, key?: string) => {
    if (!key) {
      return;
    }
    setIsDirty(true);

    const updatedData = {...datamodelData, [key]: newValue};
    setDatamodelData(updatedData);
    const isTitleFilled = alertCaseInfo?.title && alertCaseInfo.title.trim() !== "";
    if (layout && layout.layoutItems) {
      const requiredFields = layout.layoutItems.filter((item: Widget) =>
        item.id && 
        Object.prototype.hasOwnProperty.call(item, "required") &&
        ((item as IFieldWidget).required === true)
      );
      
      const allRequiredFieldsNotFilled = requiredFields
        .filter((item) => {
          const value = updatedData[item.id];
          return value === undefined || value === null || value === "";
        });
      
      const areRequiredFieldsFilled = allRequiredFieldsNotFilled.length === 0;
      
      if (isTitleFilled && areRequiredFieldsFilled) {
        setIsRequiredsDone(true);
      } else {
        setIsRequiredsDone(false);
      }
    }
  };

  const handleSaveConsolidatingEntity = () => {
    if (!alertCaseInfo || !alertCaseInfo?.consolidate_on_entity_id) {
      return;
    }
    const requiredFields: string[] = getCaptureData?.layout?.layoutItems?.filter((item: IFieldWidget) => item.required)?.map((item: IFieldWidget) => item.id) || [];
    const requiredFieldsFilled = requiredFields.every((fieldId: string) => newConsolidatingEntityData.rows[0][fieldId] !== undefined && newConsolidatingEntityData.rows[0][fieldId] !== null && newConsolidatingEntityData.rows[0][fieldId] !== "");
    if (requiredFieldsFilled) {
      setConsolidateOnEntityIsEditable((prev) => !prev);
      setConsolidatingEntityToast("");
      saveConsolidatingEntity();
    } else {
      setConsolidatingEntityToast("Please fill in all required fields.");
    }
  };

  const handleConsolidatingEntityChange = (newValue: string, key?: string) => {
    if (!key) {
      return;
    }
    setIsDirty(true);
    const rows = typeof newConsolidatingEntityData?.rows === "string" ? JSON.parse(newConsolidatingEntityData?.rows) : newConsolidatingEntityData?.rows;
    const updatedRows = rows ? rows.map((row: { id: string; }) => {
      return {...row, [key]: newValue};
    }) : [];
    setNewConsolidatingEntityData({...newConsolidatingEntityData, rows: updatedRows});
    setConsolidatingEntity(updatedRows[0]);
  };

  const handleTitleChange = (value: string) => {
    if (!alertCaseInfo) {
      return;
    }
    setIsDirty(true);
    setAlertCaseInfo({...alertCaseInfo, title: value});
    const isTitleFilled = value && value.trim() !== "";
    const requiredFields = requiredFieldsFilledIn();
    const areRequiredFieldsFilled = requiredFields.length === 0 || (requiredFields.length === 1 && requiredFields[0] === "AlertCase Title");
    
    if (isTitleFilled && areRequiredFieldsFilled) {
      setIsRequiredsDone(true);
    } else {
      setIsRequiredsDone(false);
    }
  };

  const tabs: ITab[] = [{
    name: "Actions",
    key: "actions",
    content: <Actions
      alertcaseId={params.id || ""}
      onActionPerformed={() => {
        getStageActionCheck(`stage_action_check/${alertCaseInfo?.id}/${alertCaseInfo?.current_process_type_stage_id}`);
        getFinishAlertCaseCheck(`finish_alertcase_check/${params.id}`)
      }} />
  }, {
    name: "Notes",
    key: "notes",
    content: <Notes alertcaseId={params.id || ""} />
  }, {
    name: "Documents",
    key: "documents",
    content: <Documents alertcaseId={params.id || ""} />
  }, {
    name: "History",
    key: "history",
    content: <History alertcaseId={params.id || ""} />
  }, {
    name: "Network",
    key: "network",
    content: <Network alertcaseId={params.id || ""} />
  }];

  if (validateLoadingStatusNew([getAlertCaseStatus])) {
    return <Spinner />;
  }

  if (isAuthorized !== true) {
    return (
      <div className="alertcase">
        <div className="alertcase-unauthorized">
          <div className="alertcase-toast error-toast">
            You are not authorized to view this AlertCase
          </div>
          <div className="alertcase-navigateBack">
            <Button name="backToAlertCases" className="" onClick={() => navigate("/manager/alertcases")}>
            Back to AlertCases
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  const buttonsAreDisabled = postAlertCaseStatus === "pending" || updateAlertCaseAssignUserStatus === "pending" || updateAlertCaseStateStatus === "pending" || postAlertRowDataStatus === "pending";

  return <div className="alertcase">
    <div className="alertcase-body">
      {validateLoadingStatusNew([postConsolidatingEntityDataStatus, postAlertRowDataStatus, postAlertCaseStatus, getScreenStatus]) && <Spinner />}
      <div onClick={() => handleBack()} className="alertcase-navButton">
        <BsArrowLeft />
      </div>
      <div className={`alertcase-header ${collapsed ? "collapsed" : ""}`}>
        <div className="alertcase-info">
          <h2>AlertCase Title: {alertCaseInfo?.title}</h2>
          <h2>AlertCase ID: {alertCaseInfo?.alertcase_number}</h2>
          <h2>Process Type: {alertCaseInfo?.process_type}</h2>
          <h2>Current Stage: {alertCaseInfo?.current_stage_name}</h2>

          {alertCaseInfo?.consolidate_on_entity_type ? <p> Consolidated on {alertCaseInfo?.consolidate_on_entity_type } </p> : <p>No consolidating entity</p>}
          
          <p>Domain Type: {alertCaseInfo?.domain_type}</p>

          {alertCaseInfo?.assigned_to && <p>Assigned to: {alertCaseInfo?.assigned_to === authCtx.userId ? "You" : alertCaseInfo?.assigned_to}</p>}
        </div>
        {alertCaseData && alertCaseInfo?.consolidate_on_entity_type &&
        <div className="alertcase-consolidatingEntityCapture">
          <div className={consolidateOnEntityIsEditable ? "alertcase-consolidatingEntityEditMode" : "alertcase-consolidatingEntity"}>
            <h2>Consolidating Entity ({alertCaseInfo?.consolidate_on_entity_type})</h2>
            {!blockInput && (consolidateOnEntityIsEditable ?
              <div>
                <BsSave onClick={handleSaveConsolidatingEntity} className="alertcase-consolidatingEntityEdit"/>
                <BsXLg onClick={() => {
                  setConsolidateOnEntityIsEditable((prev) => !prev);
                  setConsolidatingEntity(consolidatingEntityData.rows[0] || {});
                }} className="alertcase-consolidatingEntityEditCancel"/>
              </div>
              : <FaPencilAlt onClick={() => setConsolidateOnEntityIsEditable((prev) => !prev)} className="alertcase-consolidatingEntityEdit"/>
            )}
          </div>
          {consolidatingEntityToast && <div className="alertcase-consolidatingEntityToast">{consolidatingEntityToast}</div>}
          {consolidateOnEntityIsEditable && consolidationLayout ?
            <ReadonlyWidgetComponent dataReadonly={false} layout={consolidationLayout} data={consolidatingEntity} onDataChanged={handleConsolidatingEntityChange}/>
            : consolidationLayout &&
                <ReadonlyWidgetComponent dataReadonly={true} layout={consolidationLayout} data={consolidatingEntity}/>
          }
        </div>
        }
      </div>
      <div className="alertcase-collapseButton">
        <div className="alertcase-collapse" onClick={() => setCollapsed((prev) => !prev)}>
          {collapsed ? <MdOutlineKeyboardArrowDown /> : <MdOutlineKeyboardArrowUp />}
        </div>
      </div>

      <MultiTabComponent tabs={tabs} displayNextButton={false} />
      {layout && (
        <div className='alertcase-stage'>
          <div className="alertcase-title">
            <TextInput
              type={"text"}
              id={"alertcaseTitle"}
              required={true}
              inputKey={"alertcaseTitle"}
              label="AlertCase Title"
              handleOnChange={handleTitleChange}
              controlled={true}
              value={alertCaseInfo?.title}
            />
          </div>
          <ReadonlyWidgetComponent layout={layout} data={datamodelData} onDataChanged={handleDataChanged}/>
        </div>
      )}
    </div>
    <div className='alertcase-actionButtons'>
      {alertCaseInfo?.state !== "Finished" && (
        <div className="alertcase-actionButton">
          <ToolTip content={"Close"} direction="top" delay={350}>
            <Button disabled={buttonsAreDisabled} name="close" className="alertcase-close" onClick={() => handleSave("close")}>
                  Close
            </Button>
          </ToolTip>
        </div>
      )}
      {!blockInput && alertCaseInfo?.state !== "Finished" && (
        <div className="alertcase-actionButton">
          <ToolTip content={"Save"} direction="top" delay={350}>
            <Button disabled={buttonsAreDisabled} name="save" className="alertcase-save" onClick={() => handleSave("save")}>
                  Save
            </Button>
          </ToolTip>
        </div>
      )}
      {!blockInput && alertCaseInfo?.state !== "Finished" && (
        <div className="alertcase-actionButton">
          <ToolTip content={"Save and close"} direction="top" delay={350}>
            <Button disabled={buttonsAreDisabled} name="saveClose" className="alertcase-saveClose" onClick={() => handleSave("saveClose")}>
                  Save and close
            </Button>
          </ToolTip>
        </div>
      )}
      {!blockInput && alertCaseInfo?.state !== "Finished" && alertCaseInfo?.next_stage_id && isActionsDone && isRequiredsDone && (
        <div className="alertcase-actionButton">
          <ToolTip content={"Next stage"} direction="top" delay={350}>
            <Button disabled={buttonsAreDisabled} name="nextStage" className="alertcase-nextStage" onClick={handleNextStage}>
                  Next Stage
            </Button>
          </ToolTip>
        </div>
      )}
      {!blockInput && alertCaseInfo?.state !== "Finished" && isActionsDone && isRequiredsDone && canFinishAlertCase && (
        <div className="alertcase-actionButton">
          <ToolTip content={"Finish alertcase"} direction="top" delay={350}>
            <Button disabled={buttonsAreDisabled} name="finishAlertCase" className="alertcase-finishAlertCase" onClick={() => handleSave("finish")}>
                  Finish AlertCase
            </Button>
          </ToolTip>
        </div>
      )}
      {!blockInput && alertCaseInfo?.state === "Finished" && (
        <div className="alertcase-actionButton">
          <ToolTip content={"Re-open alertcase"} direction="top" delay={350}>
            <Button disabled={buttonsAreDisabled} name="reOpenAlertCase" className="alertcase-reOpenAlertCase" onClick={handleReOpenAlertCase}>
                  Re-open AlertCase
            </Button>
          </ToolTip>
        </div>
      )}
      {!blockInput && alertCaseInfo?.state !== "Finished" && (
        <div className="alertcase-actionButton">
          <ToolTip content={"Assign user"} direction="top" delay={350}>
            <Button disabled={buttonsAreDisabled} name="assignUser" className="alertcase-assignUser" onClick={() => setAssignUserModalVisible(true)}>
                Assign User
            </Button>
          </ToolTip>
        </div>
      )}
    </div>
    <AssignUserPopup
      handlePopupClosed={() => setAssignUserModalVisible(false)}
      show={assignUserModalVisible}
      handleAssignUser={(userId: string) => updateAlertCaseAssignUser(`assign_user/${alertCaseInfo?.id}/${userId}`)}
    />
    {flag && <ConfirmationPopup show={flag}
      handleSaveClick={() =>{ 
        handleSave("save"); 
        next();
      }}
      handlePopupClosed={() => {
        setFlag(false);
      }}
      handleContinue={() => { 
        updateAlertCaseState(`alertcase/state_change/${alertCaseInfo?.id}/${alertCaseInfo?.current_process_type_stage_id}/Open`);
        next();
        setFlag(false); }
      } 
    />
    }
    
  </div>;
};

export default AlertCase;